import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Circle, GoogleMap, Marker, Polygon, useJsApiLoader } from "@react-google-maps/api";

import Modal from "../../../components/base/Modal";
import { getMapStyles } from "../../../components/map/mapStyles";
import type { Geofence } from "../../../store/admin/geofencesStore";

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];
const MAP_HEIGHT = 420;

const isCircular = (
  coordinates: Geofence["coordinates"],
): coordinates is { latitude: number; longitude: number; radius: number } =>
  !Array.isArray(coordinates);

export interface GeofenceMapModalProps {
  geofence: Geofence;
  unitsInside: number;
  onClose: () => void;
}

export default function GeofenceMapModal({
  geofence,
  unitsInside,
  onClose,
}: GeofenceMapModalProps) {
  const { t } = useTranslation("common");
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const circular = isCircular(geofence.coordinates);

  const path = useMemo(
    () =>
      Array.isArray(geofence.coordinates)
        ? geofence.coordinates.map((point) => ({ lat: point.latitude, lng: point.longitude }))
        : [],
    [geofence.coordinates],
  );

  const center = useMemo(() => {
    if (isCircular(geofence.coordinates)) {
      return { lat: geofence.coordinates.latitude, lng: geofence.coordinates.longitude };
    }
    if (path.length === 0) return { lat: 19.0414, lng: -98.2063 };

    const sum = path.reduce(
      (total, point) => ({ lat: total.lat + point.lat, lng: total.lng + point.lng }),
      { lat: 0, lng: 0 },
    );
    return { lat: sum.lat / path.length, lng: sum.lng / path.length };
  }, [geofence.coordinates, path]);

  const radius = isCircular(geofence.coordinates) ? geofence.coordinates.radius : 0;

  const options = useMemo<google.maps.MapOptions>(
    () => ({
      styles: getMapStyles("roadmap"),
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
    }),
    [],
  );

  const shapeOptions = useMemo(
    () => ({
      strokeColor: geofence.color,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: geofence.color,
      fillOpacity: 0.18,
    }),
    [geofence.color],
  );

  const tiles = [
    {
      id: "shape",
      icon: "ri-shape-line",
      value: t(`monitoreoIa.geofences.shapes.${circular ? "circular" : "polygonal"}`),
      label: t("monitoreoIa.geofences.shape"),
    },
    {
      id: "radius",
      icon: "ri-focus-3-line",
      value: circular ? `${radius} m` : t("monitoreoIa.geofences.notApplicable"),
      label: t("monitoreoIa.geofences.radius"),
    },
    {
      id: "units",
      icon: "ri-truck-line",
      value: `${unitsInside}`,
      label: t("monitoreoIa.geofences.unitsInside"),
    },
  ];

  return (
    <Modal
      title={geofence.name}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <div className="space-y-4">
          <p className="flex items-center gap-1.5 text-xs text-content-secondary">
            <i className="ri-map-pin-2-line text-primary-500" />
            {geofence.address || t("monitoreoIa.geofences.noAddress")}
          </p>

          <div
            className="overflow-hidden rounded-xl border border-line bg-subtle"
            style={{ height: MAP_HEIGHT }}
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={center}
                zoom={circular ? 14 : 13}
                options={options}
              >
                {circular ? (
                  <>
                    <Marker position={center} title={geofence.name} />
                    <Circle center={center} radius={radius} options={shapeOptions} />
                  </>
                ) : (
                  <Polygon paths={path} options={shapeOptions} />
                )}
              </GoogleMap>
            ) : (
              <div className="flex h-full items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {tiles.map((tile) => (
              <div key={tile.id} className="rounded-xl border border-line bg-surface p-3 text-center">
                <i className={`${tile.icon} text-sm text-content-faint`} />
                <p className="mt-1 truncate text-[13px] font-bold text-content">{tile.value}</p>
                <p className="text-[10px] text-content-faint">{tile.label}</p>
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
